// Filter style classnames. The original implementation used @mui/styles makeStyles
// to attach a per-call class. Since the MUI v5→v9 upgrade removed @mui/styles, we
// inject the equivalent CSS once at module load and expose stable className strings
// from a hook so all existing consumers continue to work.
import { useTheme } from '@mui/material/styles';
import { useEffect } from 'react';

const PREFIX = 'globalFilters';

let injectedHash = null;
const themeHash = (theme) =>
  [theme.palette.mode, theme.palette.primary.main, theme.palette.text.primary, theme.palette.background.paper].join('|');

const injectStyles = (theme) => {
  if (typeof document === 'undefined') return;
  const hash = themeHash(theme);
  if (injectedHash === hash) return;
  const prev = document.getElementById(`${PREFIX}-styles`);
  if (prev) prev.remove();
  injectedHash = hash;

  const css = `
    .${PREFIX}-grid {
      max-width: ${theme.breakpoints.values['md']}px;
      padding: ${theme.spacing(2)};
      margin: auto;
    }
    .${PREFIX}-formGrid {
      padding: ${theme.spacing(1)};
      width: 100%;
      min-width: 200px;
      align-items: flex-start;
      margin: auto;
    }
    .${PREFIX}-formLabel { font-size: .75rem; }
    .${PREFIX}-fixedWidth { width: 300px; }
    .${PREFIX}-formItem { width: 100%; }
    .${PREFIX}-tinyMarginTop { margin-top: ${theme.spacing(0.5)}; }
    .${PREFIX}-smallMarginTop { margin-top: ${theme.spacing(1)}; }
    .${PREFIX}-mediumMarginTop { margin-top: ${theme.spacing(1.5)}; }
    .${PREFIX}-formControlPadding label + .MuiInput-formControl { margin-top: ${theme.spacing(0.5)}; }
    .${PREFIX}-datePickerPadding label + .MuiInput-formControl { margin-top: ${theme.spacing(2)}; }
    .${PREFIX}-datePickerPadding { width: 100%; margin-top: ${theme.spacing(1)}; }
    .${PREFIX}-hidden { display: none; }
    .${PREFIX}-icon { margin-right: ${theme.spacing(1)}; margin-left: ${theme.spacing(1)}; }
    .${PREFIX}-flexFilters {
      display: flex;
      flex-direction: column;
      flex-wrap: wrap;
      max-width: 560px;
      justify-content: space-around;
      align-items: flex-start;
    }
    .${PREFIX}-flexFilters-mobile { align-items: center; }
    .${PREFIX}-btnGroup {
      margin: 5px 0;
      background-color: transparent;
    }
    .${PREFIX}-btnGroup > button {
      border-radius: 100px;
      padding: 0px 15px;
    }
    .${PREFIX}-checkRow { display: flex; flex-direction: row; }
    .${PREFIX}-filterRow {
      display: flex;
      flex-direction: row;
      align-items: center;
    }
    .${PREFIX}-filterRow .FilterBtn { margin-right: ${theme.spacing(1)}; }
    .${PREFIX}-filterRowMobile { flex-direction: column; align-items: flex-start; }
    .${PREFIX}-checkIcon {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      color: ${theme.palette.text.primary};
      margin-right: ${theme.spacing(1)};
    }
    .${PREFIX}-checkIcon svg { color: inherit; fill: currentColor; }
    .${PREFIX}-smallText { line-height: 1.2; }
    .${PREFIX}-smallText div { display: contents; font-size: 0.7rem; }
    .${PREFIX}-linkText {
      cursor: pointer;
      font-size: .75rem;
      white-space: nowrap;
      min-width: auto;
    }
    .${PREFIX}-compareRow { display: flex; flex-direction: row; width: 100%; }
    .${PREFIX}-compareClose { height: 35px; position: relative; top: 7px; }
  `;

  const tag = document.createElement('style');
  tag.id = `${PREFIX}-styles`;
  tag.textContent = css;
  document.head.appendChild(tag);
};

const classes = {
  grid: `${PREFIX}-grid`,
  formGrid: `${PREFIX}-formGrid`,
  formLabel: `${PREFIX}-formLabel`,
  fixedWidth: `${PREFIX}-fixedWidth`,
  mobileFormContainer: `${PREFIX}-mobileFormContainer`,
  formItem: `${PREFIX}-formItem`,
  tinyMarginTop: `${PREFIX}-tinyMarginTop`,
  smallMarginTop: `${PREFIX}-smallMarginTop`,
  mediumMarginTop: `${PREFIX}-mediumMarginTop`,
  formControlPadding: `${PREFIX}-formControlPadding`,
  datePickerPadding: `${PREFIX}-datePickerPadding`,
  dateRangeRowPadding: `${PREFIX}-dateRangeRowPadding`,
  hidden: `${PREFIX}-hidden`,
  icon: `${PREFIX}-icon`,
  btnGroup: `${PREFIX}-btnGroup`,
  checkRow: `${PREFIX}-checkRow`,
  checkIcon: `${PREFIX}-checkIcon`,
  smallText: `${PREFIX}-smallText`,
  linkText: `${PREFIX}-linkText`,
  compareRow: `${PREFIX}-compareRow`,
  compareClose: `${PREFIX}-compareClose`,
  filterRowMobile: `${PREFIX}-filterRowMobile`,
  form: '',
};

const useStyles = (opts = {}) => {
  const theme = useTheme();
  useEffect(() => {
    injectStyles(theme);
  }, [theme]);

  // Inject immediately if possible so styles are available on first render.
  injectStyles(theme);

  return {
    ...classes,
    flexFilters: opts.isMobile ? `${PREFIX}-flexFilters ${PREFIX}-flexFilters-mobile` : `${PREFIX}-flexFilters`,
    filterRow: opts.isMobile ? `${PREFIX}-filterRow ${PREFIX}-filterRowMobile` : `${PREFIX}-filterRow`,
  };
};

export default useStyles;
